import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import SortSavedItemsModal from './SortSavedItemsModal'
import ItemCard from '../ItemCard/ItemCard'

import {
  tripDrawerSavedItemsListFilterOptionSelected,
  tripDrawerSavedItemsListSortOptionSelected,
  tripDrawerSavedItemsListTripClicked,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import FilterChip from 'components/Luxkit/Chips/FilterChip'
import DeprecatedDropdown from 'components/Luxkit/Dropdown/DeprecatedDropdown'
import DeprecatedDropdownContainer from 'components/Luxkit/Dropdown/DeprecatedDropdownContainer'
import LineAngleDownIcon from 'components/Luxkit/Icons/line/LineAngleDownIcon'
import ListItem from 'components/Luxkit/List/ListItem'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import useNavigateTo from 'hooks/useNavigateTo'
import useToggle from 'hooks/useToggle'
import { sortBy } from 'lib/array/arrayUtils'
import { capitalise } from 'lib/string/stringUtils'
import { SAVED_ITEMS_LIST_LAST_SELECTED_SORT_OPTION_STORAGE_KEY } from 'tripPlanner/config'
import TripContext, { TripContextValue } from 'tripPlanner/contexts/TripContext'
import { BasicTrip } from 'tripPlanner/types/common'
import { TripItem } from 'tripPlanner/types/tripItem'
import { getTripPermissions } from 'tripPlanner/utils'
import { filterProducts, filterStatus } from 'tripPlanner/utils/summary'

export const TRIP_ITEMS_SORT_OPTIONS = [
  'recently added',
  'alphabetical',
  'by trip',
] as const

export type TripItemsSortOptions = (typeof TRIP_ITEMS_SORT_OPTIONS)[number]

interface Props {
  trips: BasicTrip[]
  tripItems: TripItem[]
  filters: App.TripPlannerSubFilterType[]
}

export default function SavedItemsList({ trips, filters, tripItems }: Props) {
  const [sortOption, setSortOption] =
    useState<TripItemsSortOptions>('recently added')
  const sortChipRef = useRef(null)
  const { value: isDropdownOpen, toggle: toggleDropdown } = useToggle()

  const tripNamePermissionsMap: Record<
    string,
    { name: string; context: TripContextValue }
  > = useMemo(() => {
    return trips.reduce((map, trip) => {
      map[trip.id] = {
        name: trip.name,
        context: {
          tripId: trip.id,
          viewAsTemplate: false,
          isPublicTrip: false,
          permissions: getTripPermissions(trip),
          name: trip.name,
        },
      }
      return map
    }, {})
  }, [trips])

  const showModal = useContext(ModalContext)

  const onApplySortOption = useCallback(
    (sortOption: TripItemsSortOptions) => {
      setSortOption(sortOption)
      toggleDropdown()
      localStorage.setItem(
        SAVED_ITEMS_LIST_LAST_SELECTED_SORT_OPTION_STORAGE_KEY,
        JSON.stringify(sortOption),
      )
      fireInteractionEvent(
        tripDrawerSavedItemsListSortOptionSelected(sortOption),
      )
    },
    [setSortOption, toggleDropdown],
  )

  const showSortItemsModal = useCallback(() => {
    showModal(
      <SortSavedItemsModal
        onSelect={onApplySortOption}
        selectedSortOption={sortOption}
      />,
    )
  }, [onApplySortOption, showModal, sortOption])

  const processedTripItems = useMemo(() => {
    let processed = [...tripItems]
    const filtersSet = new Set(filters)
    if (filters.length > 0) {
      processed = processed.filter(
        (item) =>
          filterProducts(item, filtersSet) || filterStatus(item, filtersSet),
      )
    }
    switch (sortOption) {
      case 'recently added':
        processed = sortBy(processed, (item) => item.createdAt, 'desc')
        break
      case 'alphabetical':
        processed = sortBy(processed, (item) => item.sortableName, 'asc')
        break
      case 'by trip':
        processed = sortBy(
          processed,
          (item) => tripNamePermissionsMap[item.tripId].name,
          'asc',
        )
        break
    }
    return processed
  }, [filters, sortOption, tripItems, tripNamePermissionsMap])

  // Intialise stored sort & filter options
  useIsomorphicLayoutEffect(() => {
    const storedSortOption = localStorage.getItem(
      SAVED_ITEMS_LIST_LAST_SELECTED_SORT_OPTION_STORAGE_KEY,
    )

    if (storedSortOption) {
      const parsedOption = JSON.parse(storedSortOption)
      if (TRIP_ITEMS_SORT_OPTIONS.includes(parsedOption)) {
        setSortOption(parsedOption)
      }
    }
  }, [])

  const navigateTo = useNavigateTo()
  const modalElement = useModalElementContext()
  const onClickCard = useCallback(
    (offerLink: string) => {
      navigateTo(offerLink)
      modalElement.resolve()
    },
    [modalElement, navigateTo],
  )

  const onTripClick = useCallback(
    (tripId: string) => {
      fireInteractionEvent(tripDrawerSavedItemsListTripClicked())
      navigateTo(`/trip-planner/trip/${tripId}/summary`)
      modalElement?.resolve()
    },
    [navigateTo, modalElement],
  )
  const isFiltersTrackable = useRef<boolean>(false)
  useEffect(() => {
    if (filters.length > 0) {
      // After the first time a filter has been selected we can track filter changes
      // This avoids tracking 'all' on render
      isFiltersTrackable.current = true
    }
    if (isFiltersTrackable.current) {
      fireInteractionEvent(
        tripDrawerSavedItemsListFilterOptionSelected(filters),
      )
    }
  }, [filters])

  return (
    <Group direction="vertical" gap={24}>
      {processedTripItems.length > 1 && (
        <Group
          direction="horizontal"
          horizontalAlign="space-between"
          verticalAlign="center"
          gap={12}
        >
          <div />
          <CSSBreakpoint max="mobile">
            <FilterChip
              ref={sortChipRef}
              style={{ zIndex: 1 }}
              onClick={showSortItemsModal}
              size="medium"
              endIcon={<LineAngleDownIcon />}
            >
              Sort: {capitalise(sortOption)}
            </FilterChip>
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet">
            <FilterChip
              ref={sortChipRef}
              style={{ zIndex: 1 }}
              onClick={toggleDropdown}
              size="medium"
              endIcon={<LineAngleDownIcon />}
            >
              Sort: {capitalise(sortOption)}
            </FilterChip>
            <DeprecatedDropdown
              position="bottom"
              open={isDropdownOpen}
              triggerRef={sortChipRef}
              onClose={toggleDropdown}
            >
              <DeprecatedDropdownContainer fit="dropdown-items">
                {TRIP_ITEMS_SORT_OPTIONS.map((opt) => (
                  <ListItem
                    key={opt}
                    onClick={() => onApplySortOption(opt)}
                    title={capitalise(opt)}
                    selected={sortOption === opt}
                  />
                ))}
              </DeprecatedDropdownContainer>
            </DeprecatedDropdown>
          </CSSBreakpoint>
        </Group>
      )}
      <Group direction="vertical" gap={24}>
        {processedTripItems.map((item) => (
          <TripContext.Provider
            key={item.id}
            value={tripNamePermissionsMap[item.tripId].context}
          >
            <ItemCard
              item={item}
              onClickCard={onClickCard}
              onClickTripName={() => onTripClick(item.tripId)}
              variant="drawer"
            />
          </TripContext.Provider>
        ))}
      </Group>
    </Group>
  )
}
